import { useState, useEffect, useCallback } from "react";
import {
  View,
  Text,
  ScrollView,
  Pressable,
  ActivityIndicator,
  RefreshControl,
  Image,
} from "react-native";
import { SafeAreaView } from "react-native-safe-area-context";
import { Ionicons } from "@expo/vector-icons";
import { useRouter } from "expo-router";
import { useAuth } from "@/providers/AuthProvider";
import { useTheme, useThemeColors } from "@/providers/ThemeProvider";
import { supabase } from "@/lib/supabase/client";
import { Team, Profile } from "@/types";

/**
 * Team with members and rank
 */
interface RankedTeam extends Team {
  rank: number;
  member_count: number;
  members: Pick<Profile, "id" | "full_name" | "avatar_url">[];
  is_user_team: boolean;
}

/**
 * Podium position component
 */
interface PodiumProps {
  team: RankedTeam | undefined;
  position: 1 | 2 | 3;
}

const Podium = ({ team, position }: PodiumProps) => {
  const router = useRouter();
  const colors = useThemeColors();
  
  if (!team) return null;

  const isFirst = position === 1;
  const teamColor = team.color || colors.primary;

  const getBadgeColor = () => {
    switch (position) {
      case 1:
        return "#FFD700"; // Gold
      case 2:
        return "#C0C0C0"; // Silver
      case 3:
        return "#CD7F32"; // Bronze
    }
  };

  /**
   * Render team avatar - handles URL images, emojis, or fallback to letter
   */
  const renderAvatar = () => {
    if (team.avatar_url) {
      // Check if it's a URL (http/https) or an emoji
      if (team.avatar_url.startsWith("http")) {
        return (
          <Image
            source={{ uri: team.avatar_url }}
            className="w-full h-full"
            resizeMode="cover"
          />
        );
      } else {
        // It's an emoji
        return (
          <Text className={isFirst ? "text-4xl" : "text-3xl"}>
            {team.avatar_url}
          </Text>
        );
      }
    }
    // Fallback to first letter
    return (
      <Text
        className={`font-bold ${isFirst ? "text-3xl" : "text-2xl"}`}
        style={{ color: teamColor }}
      >
        {team.name.charAt(0).toUpperCase()}
      </Text>
    );
  };

  const getRankLabel = () => {
    switch (position) {
      case 1: return "1e";
      case 2: return "2e";
      case 3: return "3e";
    }
  };

  return (
    <Pressable
      onPress={() => router.push(`/(tabs)/teams/${team.id}` as any)}
      className="flex-1 items-center"
      accessibilityRole="button"
      accessibilityLabel={`${team.name}, positie ${position}, ${team.total_score} punten`}
    >
      <View className="relative">
        <View
          className={`rounded-full items-center justify-center mb-2 border-4 overflow-hidden ${
            isFirst ? "w-20 h-20" : "w-16 h-16"
          }`}
          style={{
            backgroundColor: `${teamColor}20`,
            borderColor: getBadgeColor(),
          }}
        >
          {renderAvatar()}
        </View>
        {/* Rank badge */}
        <View
          className="absolute -bottom-0.5 left-1/2 rounded-full px-1.5 py-0.5 items-center justify-center"
          style={{
            backgroundColor: getBadgeColor(),
            transform: [{ translateX: -14 }],
            minWidth: 28,
          }}
        >
          <Text className="text-xs font-bold" style={{ color: position === 2 ? "#333" : "#FFFFFF" }}>
            {getRankLabel()}
          </Text>
        </View>
      </View>
      <View className="items-center mt-1 px-1" style={{ maxWidth: "100%" }}>
        <Text
          className={`text-center ${isFirst ? "font-bold text-sm" : "font-semibold text-xs"}`}
          style={{ color: colors.text }}
          numberOfLines={2}
        >
          {team.name}
        </Text>
        <Text
          className={`${isFirst ? "text-sm font-semibold" : "text-xs"}`}
          style={{ color: teamColor }}
        >
          {team.total_score} pts
        </Text>
      </View>
    </Pressable>
  );
};

/**
 * Team list item component
 */
interface TeamItemProps {
  team: RankedTeam;
  onPress: () => void;
}

const TeamItem = ({ team, onPress }: TeamItemProps) => {
  const colors = useThemeColors();
  const teamColor = team.color || colors.primary;

  const getRankColor = () => {
    switch (team.rank) {
      case 1:
        return "#FFD700";
      case 2:
        return "#C0C0C0";
      case 3:
        return "#CD7F32";
      default:
        return colors.textTertiary;
    }
  };

  /**
   * Render team avatar - handles URL images, emojis, or fallback to letter
   */
  const renderAvatar = () => {
    if (team.avatar_url) {
      // Check if it's a URL (http/https) or an emoji
      if (team.avatar_url.startsWith("http")) {
        return (
          <Image
            source={{ uri: team.avatar_url }}
            className="w-full h-full"
            resizeMode="cover"
          />
        );
      } else {
        // It's an emoji
        return <Text className="text-xl">{team.avatar_url}</Text>;
      }
    }
    // Fallback to first letter
    return (
      <Text className="font-bold" style={{ color: teamColor }}>
        {team.name.charAt(0).toUpperCase()}
      </Text>
    );
  };

  return (
    <Pressable
      onPress={onPress}
      className="rounded-xl p-4 mb-3 flex-row items-center active:opacity-90"
      style={{
        backgroundColor: team.is_user_team ? `${teamColor}10` : colors.surface,
        borderWidth: team.is_user_team ? 2 : 1,
        borderColor: team.is_user_team ? teamColor : colors.border,
      }}
      accessibilityRole="button"
      accessibilityLabel={`Team ${team.name}, positie ${team.rank}, ${team.total_score} punten`}
    >
      <Text
        className="text-lg font-bold mr-4 w-6"
        style={{ color: getRankColor() }}
      >
        {team.rank}
      </Text>
      <View
        className="w-10 h-10 rounded-full mr-3 items-center justify-center overflow-hidden"
        style={{ backgroundColor: `${teamColor}20` }}
      >
        {renderAvatar()}
      </View>
      <View className="flex-1">
        <View className="flex-row items-center">
          <Text className="font-semibold" style={{ color: colors.text }}>
            {team.name}
          </Text>
          {team.is_user_team && (
            <View
              className="rounded px-1.5 py-0.5 ml-2"
              style={{ backgroundColor: colors.primaryLight }}
            >
              <Text className="text-xs font-medium" style={{ color: colors.primary }}>
                Jouw team
              </Text>
            </View>
          )}
        </View>
        <Text className="text-sm" style={{ color: colors.textSecondary }}>
          {team.member_count} {team.member_count === 1 ? "lid" : "leden"}
        </Text>
      </View>
      <Text className="font-bold" style={{ color: colors.textSecondary }}>
        {team.total_score} pts
      </Text>
    </Pressable>
  );
};

/**
 * Teams Screen - Scoreboard and team rankings
 */
export default function TeamsScreen() {
  const router = useRouter();
  const { activeWeekend, activeProfile, userTeam } = useAuth();
  const colors = useThemeColors();

  const [teams, setTeams] = useState<RankedTeam[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);

  /**
   * Fetch teams with rankings
   */
  const fetchTeams = useCallback(async () => {
    if (!activeWeekend) {
      setIsLoading(false);
      return;
    }

    try {
      // Fetch teams with members
      const { data: teamsData, error: teamsError } = await supabase
        .from("teams")
        .select(`
          *,
          team_members (
            user_id,
            profiles:user_id (
              id,
              full_name,
              avatar_url
            )
          )
        `)
        .eq("weekend_id", activeWeekend.id)
        .order("total_score", { ascending: false });

      if (teamsError) throw teamsError;

      // Transform and rank teams
      const rankedTeams: RankedTeam[] = (teamsData || []).map((team: any, index: number) => ({
        ...team,
        rank: index + 1,
        member_count: team.team_members?.length || 0,
        members: team.team_members?.map((tm: any) => tm.profiles).filter(Boolean) || [],
        is_user_team: userTeam?.id === team.id,
      }));

      setTeams(rankedTeams);
    } catch (error) {
      console.error("Error fetching teams:", error);
    } finally {
      setIsLoading(false);
      setIsRefreshing(false);
    }
  }, [activeWeekend, userTeam]);

  useEffect(() => {
    fetchTeams();
  }, [fetchTeams]);

  /**
   * Handle refresh
   */
  const handleRefresh = () => {
    setIsRefreshing(true);
    fetchTeams();
  };

  /**
   * Handle team press
   */
  const handleTeamPress = (team: RankedTeam) => {
    router.push(`/(tabs)/teams/${team.id}` as any);
  };

  // No active weekend
  if (!activeWeekend) {
    return (
      <SafeAreaView
        className="flex-1 items-center justify-center px-8"
        style={{ backgroundColor: colors.background }}
        edges={["top", "left", "right"]}
      >
        <Ionicons name="calendar-outline" size={64} color={colors.border} />
        <Text
          className="text-xl font-semibold mt-4 text-center"
          style={{ color: colors.text }}
        >
          Geen actief weekend
        </Text>
        <Text className="text-center mt-2" style={{ color: colors.textSecondary }}>
          Er is momenteel geen familie weekend actief. Teams worden zichtbaar zodra
          een weekend is gestart.
        </Text>
      </SafeAreaView>
    );
  }

  // Loading
  if (isLoading) {
    return (
      <SafeAreaView
        className="flex-1 items-center justify-center"
        style={{ backgroundColor: colors.background }}
        edges={["top", "left", "right"]}
      >
        <ActivityIndicator size="large" color={colors.primary} />
        <Text className="mt-2" style={{ color: colors.textSecondary }}>
          Teams laden...
        </Text>
      </SafeAreaView>
    );
  }

  // No teams yet
  if (teams.length === 0) {
    return (
      <SafeAreaView
        className="flex-1 items-center justify-center px-8"
        style={{ backgroundColor: colors.background }}
        edges={["top", "left", "right"]}
      >
        <Ionicons name="people-outline" size={64} color={colors.border} />
        <Text
          className="text-xl font-semibold mt-4 text-center"
          style={{ color: colors.text }}
        >
          Nog geen teams
        </Text>
        <Text className="text-center mt-2" style={{ color: colors.textSecondary }}>
          De familie admins zijn nog bezig met het aanmaken van teams. Check later
          nog eens!
        </Text>
        <Pressable
          onPress={handleRefresh}
          className="mt-6 rounded-xl px-6 py-3 flex-row items-center"
          style={{ backgroundColor: colors.primaryLight }}
          accessibilityRole="button"
          accessibilityLabel="Vernieuwen"
        >
          <Ionicons name="refresh-outline" size={20} color={colors.primary} />
          <Text className="font-medium ml-2" style={{ color: colors.primary }}>
            Vernieuwen
          </Text>
        </Pressable>
      </SafeAreaView>
    );
  }

  // Get podium teams
  const firstPlace = teams.find((t) => t.rank === 1);
  const secondPlace = teams.find((t) => t.rank === 2);
  const thirdPlace = teams.find((t) => t.rank === 3);

  return (
    <SafeAreaView className="flex-1" style={{ backgroundColor: colors.background }} edges={["top", "left", "right"]}>
      <ScrollView
        className="flex-1"
        showsVerticalScrollIndicator={false}
        refreshControl={
          <RefreshControl
            refreshing={isRefreshing}
            onRefresh={handleRefresh}
            tintColor={colors.primary}
          />
        }
      >
        {/* Header */}
        <View className="px-4 py-4">
          <Text className="text-2xl font-bold" style={{ color: colors.text }}>
            Scoreboard
          </Text>
          <Text className="mt-1" style={{ color: colors.textSecondary }}>
            {activeWeekend.name} • {teams.length} teams
          </Text>
        </View>

        {/* Podium */}
        {teams.length >= 2 && (
          <View className="px-6 py-6 flex-row items-end justify-center gap-2">
            <Podium team={secondPlace} position={2} />
            <Podium team={firstPlace} position={1} />
            {thirdPlace && <Podium team={thirdPlace} position={3} />}
          </View>
        )}

        {/* My Team Section */}
        {userTeam && (
          <View className="px-4 mt-4">
            <Text className="text-lg font-bold mb-3" style={{ color: colors.text }}>
              Jouw Team
            </Text>
            {teams
              .filter((t) => t.is_user_team)
              .map((team) => (
                <TeamItem
                  key={team.id}
                  team={team}
                  onPress={() => handleTeamPress(team)}
                />
              ))}
          </View>
        )}

        {/* All Teams List */}
        <View className="px-4 mt-4">
          <Text className="text-lg font-bold mb-3" style={{ color: colors.text }}>
            Alle teams
          </Text>
          {teams.map((team) => (
            <TeamItem
              key={team.id}
              team={team}
              onPress={() => handleTeamPress(team)}
            />
          ))}
        </View>

        <View className="h-8" />
      </ScrollView>
    </SafeAreaView>
  );
}
